import { createModel } from '@rematch/core';
import { toast } from 'react-toastify';

// Constants
import URL from '../../constants/api-constants';

// Types
import type { RootModel } from './index';

// Http
import http from '../http/http-common';
import {
  IPreHeatRecipe,
  IPreHeatRecipeEditable,
} from '../../types/recipes-types';
import { AxiosError } from 'axios';
import { generatePath } from 'react-router-dom';

const DEFAULT_STATE: {
  preHeatRecipes: IPreHeatRecipeEditable[];
  preHeatRecipe: IPreHeatRecipeEditable | null;
} = {
  preHeatRecipes: [],
  preHeatRecipe: null,
};

export const pre_heat_recipes = createModel<RootModel>()({
  state: DEFAULT_STATE,
  reducers: {
    set(state, payload) {
      return {
        ...state,
        ...payload,
      };
    },
    resetOne(state) {
      return {
        ...state,
        preHeatRecipe: DEFAULT_STATE.preHeatRecipe,
      };
    },
  },
  effects: (dispatch) => {
    return {
      async createPreHeatRecipe(payload: IPreHeatRecipeEditable) {
        try {
          const response = await http.post(URL.PRE_HEAT_RECIPES, payload);
          toast.success('Recipe created successfully');
          return response.data;
        } catch (error: any) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
        }
      },
      
      async createPreHeatRecipeFromScratch(payload: IPreHeatRecipeEditable) {
        try {
          await http.post(URL.PRE_HEAT_RECIPES, payload);
          toast.success('Recipe created successfully');
        } catch (error: any) {
          toast.error(error.message);
        }
      },

      async getPreHeatRecipes(): Promise<IPreHeatRecipe[]> {
        try {
          const response = await http.get(URL.PRE_HEAT_RECIPES);

          dispatch.pre_heat_recipes.set({
            preHeatRecipes: response.data,
          });

          return response.data;
        } catch (error: any) {
          toast.error(error.message);
          return [];
        }
      },

      async getPreHeatRecipe(id: string): Promise<IPreHeatRecipe> {
        try {
          const response = await http.get(generatePath(URL.PRE_HEAT_RECIPE, { id }));

          dispatch.pre_heat_recipes.set({
            preHeatRecipe: response.data,
          });

          return response.data
        } catch (error: any) {
          toast.error(error.message);
        }
      },

      async removePreHeatRecipe(id: string) {
        try {
          await http.delete(generatePath(URL.PRE_HEAT_RECIPE, { id }));
          toast.success('Recipe deleted successfully');
        } catch (error: any) {
          toast.error(error.message);
        }
      },

      async updatePreHeatRecipe(payload: { id: string; body: Partial<IPreHeatRecipeEditable> }) {
        try {
          await http.put(generatePath(URL.PRE_HEAT_RECIPE, { id: payload.id }), payload.body);
          toast.success('Recipe updated successfully');
        } catch (error: any) {
          toast.error(error.message);
        }
      },
    };
  },
});
